import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  ChevronLeft,
  Download,
  Share2,
  Loader2,
  ExternalLink,
  FileText,
  RefreshCw
} from "lucide-react";

const PayslipViewer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, logout } = useAuth();
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const fetchPayslip = useCallback(async () => {
    if (!token || !id) {
      setLoading(false);
      return;
    }
    
    setError(null);
    try {
      const response = await axios.get(`${API}/employee/payslip/${id}`, {
        params: { token },
        timeout: 15000
      });
      setPayslip(response.data);
      
      // Create blob URL for PDF
      if (response.data.pdf_base64) {
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      }
      
    } catch (error) {
      console.error("Error fetching payslip:", error);
      if (error.response?.status === 401) {
        toast.error("Sessão expirada");
        logout();
        navigate("/");
      } else if (error.response?.status === 404) {
        setError("Contracheque não encontrado");
      } else {
        setError("Erro ao carregar contracheque");
      }
    } finally {
      setLoading(false);
    }
  }, [id, token, logout, navigate]);

  useEffect(() => {
    fetchPayslip();
    
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [fetchPayslip]);

  const handleDownload = () => {
    if (!payslip || !pdfUrl) return;
    
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `contracheque_${payslip.mes_nome}_${payslip.ano}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("Download iniciado!");
  };

  const handleShare = async () => {
    if (!payslip || !payslip.pdf_base64) return;
    
    try {
      const byteCharacters = atob(payslip.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const file = new File([blob], `contracheque_${payslip.mes_nome}_${payslip.ano}.pdf`, { type: 'application/pdf' });
      
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Contracheque - ${payslip.mes_nome}/${payslip.ano}`,
        });
      } else {
        handleDownload();
        toast.info("Baixe o arquivo para compartilhar");
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        handleDownload();
      }
    }
  };

  const handleOpenPdf = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handleBack = () => {
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-900 mx-auto mb-4" />
          <p className="text-slate-600 text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <header className="bg-white border-b border-slate-200 px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="h-9 w-9 p-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
        </header>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">{error}</p>
            <Button variant="outline" onClick={handleBack}>
              Voltar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!payslip) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="h-9 w-9 p-0"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <p className="font-semibold text-slate-900">
                  {payslip.mes_nome} / {payslip.ano}
                </p>
                <p className="text-xs text-slate-500">
                  {({
                    diferenca: "Diferença Salarial",
                    "13o": "13º Salário",
                    ferias: "Férias",
                    complementar: "Complementar",
                  })[payslip.tipo] || "Contracheque"}
                </p>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenPdf}
              className="h-9"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Abrir
            </Button>
          </div>
        </div>
      </header>

      {/* PDF Viewer */}
      <main className="flex-1 overflow-hidden bg-slate-200">
        {pdfUrl && (
          <object
            data={pdfUrl}
            type="application/pdf"
            className="w-full h-full"
            style={{ minHeight: 'calc(100vh - 180px)' }}
          >
            {/* Fallback for mobile */}
            <div className="flex flex-col items-center justify-center h-full min-h-[60vh] p-6 text-center bg-slate-100">
              <div className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center mb-4">
                <FileText className="w-10 h-10 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Contracheque Disponível
              </h3>
              <p className="text-slate-500 text-sm mb-6 max-w-xs">
                {payslip.mes_nome} de {payslip.ano}
              </p>
              <div className="space-y-3 w-full max-w-xs">
                <Button
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                  onClick={handleOpenPdf}
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Visualizar PDF
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-12"
                  onClick={handleDownload}
                >
                  <Download className="w-5 h-5 mr-2" />
                  Baixar PDF
                </Button>
              </div>
            </div>
          </object>
        )}
      </main>

      {/* Bottom Action Bar */}
      <div className="bg-white border-t border-slate-200 sticky bottom-0 z-50">
        <div className="px-4 py-3 pb-safe">
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="h-12"
              onClick={handleShare}
              data-testid="share-btn"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Compartilhar
            </Button>
            <Button
              className="h-12 bg-emerald-600 hover:bg-emerald-700"
              onClick={handleDownload}
              data-testid="download-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Baixar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayslipViewer;
